import { colors } from '@atlaskit/theme';
import { getTokenValue } from '@atlaskit/tokens';
import { fontFallback } from '@atlaskit/theme/typography';
import { DEFAULT_EDGE_LENGTH, DEFAULT_REPLUSION, DEFAULT_SYMBOL_SIZE } from '../../constants.tsx';

import type { EChartsEvent } from '../../types.tsx';

const defaultNodeLabelOverflow = {
	overflow: 'breakAll',
	width: 150,
};

const DARK_MODE_TEXT_COLOR = '#9fadbc';
const DEFAULT_NODE_LABEL_LIGHT_MODE = {
	fontFamily: getTokenValue(
		'font.family.body',
		"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
	),
	show: true,
	position: 'bottom',
	// Can't convert this to tokens/css-vars as it ends up in a 'canvas' context
	color: colors.N700,
	font: getTokenValue('font.body.small', fontFallback.body.small),
	...defaultNodeLabelOverflow,
} as const;

const DEFAULT_NODE_LABEL_DARK_MODE = {
	...DEFAULT_NODE_LABEL_LIGHT_MODE,
	color: DARK_MODE_TEXT_COLOR,
} as const;

export const getSeriesForce = (enableAnimation: boolean) => ({
	initLayout: 'circular',
	repulsion: DEFAULT_REPLUSION,
	edgeLength: DEFAULT_EDGE_LENGTH,
	friction: 1,
	layoutAnimation: enableAnimation,
});

// Options available at https://echarts.apache.org/en/option.html#series-graph.force
export const getDefaultSeriesConfig = (enableAnimation: boolean, isDarkMode: boolean) => ({
	type: 'graph',
	layout: 'force',
	roam: 'move',
	label: isDarkMode ? DEFAULT_NODE_LABEL_DARK_MODE : DEFAULT_NODE_LABEL_LIGHT_MODE,
	edgeSymbol: ['none', 'arrow'],
	symbolSize: DEFAULT_SYMBOL_SIZE,
	force: getSeriesForce(enableAnimation),
	emphasis: {
		scale: false,
	},
	animation: enableAnimation,
});

const escapeHtml = (unsafe: string) =>
	unsafe
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');

// Options available at https://echarts.apache.org/en/option.html#series-graph
export const DEFAULT_GRAPH_OPTIONS = {
	tooltip: {
		padding: 4,
		// Can't convert this to tokens/css-vars as it ends up in a 'canvas' context
		backgroundColor: colors.N800,
		borderColor: '#333333',
		textStyle: {
			// Can't convert this to tokens/css-vars as it ends up in a 'canvas' context
			color: colors.N0,
			fontFamily: getTokenValue(
				'font.family.body',
				"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
			),
			// Can't convert this to tokens/css-vars as it ends up in a 'canvas' context
			fontSize: 11,
		},
		formatter: ({ data }: EChartsEvent) => {
			// @ts-expect-error - TS2339 - Property 'label' does not exist on type 'EchartsGraphNode'.
			const { name = '', label = '' } = data ?? {};
			return escapeHtml(name ?? label);
		},
	},
} as const;

export const SELECTED_NODE_OPTIONS = {
	label: {
		fontFamily: getTokenValue(
			'font.family.body',
			"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
		),
		fontWeight: getTokenValue('font.weight.bold', '700'),
		fontSize: 13,
		// don't truncate label when node is selected
		formatter: ({ name }: EChartsEvent) => name,
	},
} as const;

export const getDefaultNodeOptions = (isDarkMode: boolean) => ({
	label: isDarkMode ? DEFAULT_NODE_LABEL_DARK_MODE : DEFAULT_NODE_LABEL_LIGHT_MODE,
});

export const DEFAULT_LINE_STYLE = {
	width: 0.8,
	opacity: 1,
	type: 'solid',
	curveness: 0.1,
} as const;

// Prevents node from trying to recalculate it's layout in relation to other surrounding nodes on update
export const FIXED_NODE = { fixed: true } as const;
export const UNFIXED_NODE = { fixed: false } as const;

export const DEFAULT_STYLES = { height: '100%', width: '100%' } as const;
